import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueries, useQueryClient } from 'react-query';
import { getApiNamesByService, getApiMetadata, ApiMetadata } from '../api/services';
import { createConcurrencyLimiter } from '../utils/concurrency-limiter';

const MAX_CONCURRENT_DETAIL_REQUESTS = 3;
const SERVICE_API_STALE_TIME_MS = 5 * 60 * 1000;

// Shared across hook instances so the cap holds for the whole page, not per render.
const runLimitedDetailRequest = createConcurrencyLimiter(MAX_CONCURRENT_DETAIL_REQUESTS);

export type ApiDetailStatus = { state: 'loading' } | { state: 'loaded' } | { state: 'failed'; message: string };

interface UseServiceApisResult {
  apis: ApiMetadata[];
  detailStatusByApiName: Record<string, ApiDetailStatus>;
  isLoading: boolean;
  isLoadingDetails: boolean;
  loadingProgress: { loaded: number; total: number };
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
  retryApiDetails: (apiName: string) => void;
}

const serviceApisQueryKey = (serviceName: string) => ['service-apis', serviceName] as const;
const apiNamesQueryKey = (serviceName: string) => [...serviceApisQueryKey(serviceName), 'names'] as const;
const apiDetailQueryKey = (serviceName: string, apiName: string) =>
  [...serviceApisQueryKey(serviceName), 'detail', apiName] as const;

function describeFailure(action: string, error: unknown): string {
  const cause = error instanceof Error ? error.message : String(error);
  return `Could not ${action}: ${cause}`;
}

// API names load page by page; every listed API's metadata then loads in the background (a few at a
// time) as its own query, so each API has independent loading, error, and retry state.
export function useServiceApis(serviceName: string): UseServiceApisResult {
  const queryClient = useQueryClient();

  const apiNamesQuery = useInfiniteQuery(
    apiNamesQueryKey(serviceName),
    ({ pageParam }) => getApiNamesByService(serviceName, pageParam),
    {
      enabled: Boolean(serviceName),
      staleTime: SERVICE_API_STALE_TIME_MS,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
    },
  );

  const apiNames = useMemo(
    () => apiNamesQuery.data?.pages.flatMap((page) => page.apiNames) ?? [],
    [apiNamesQuery.data],
  );

  const apiDetailQueries = useQueries(
    apiNames.map((apiName) => ({
      queryKey: apiDetailQueryKey(serviceName, apiName),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        runLimitedDetailRequest(() => getApiMetadata(serviceName, apiName, signal), signal),
      staleTime: SERVICE_API_STALE_TIME_MS,
    })),
  );

  const apis = apiNames.map(
    (apiName, position): ApiMetadata => apiDetailQueries[position]?.data ?? { serviceName, apiName },
  );

  const detailStatusByApiName: Record<string, ApiDetailStatus> = {};
  apiNames.forEach((apiName, position) => {
    const detailQuery = apiDetailQueries[position];
    detailStatusByApiName[apiName] = detailQuery?.isError
      ? {
          state: 'failed',
          message: describeFailure(`load details for ${apiName} in ${serviceName}`, detailQuery.error),
        }
      : detailQuery?.isSuccess
        ? { state: 'loaded' }
        : { state: 'loading' };
  });

  const loadedDetailCount = Object.values(detailStatusByApiName).filter((status) => status.state !== 'loading').length;

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = apiNamesQuery;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Resetting clears the cached names and details for this service and refetches from the first page.
  const refresh = useCallback(() => {
    queryClient.resetQueries(serviceApisQueryKey(serviceName));
  }, [queryClient, serviceName]);

  const retryApiDetails = useCallback(
    (apiName: string) => {
      queryClient.refetchQueries(apiDetailQueryKey(serviceName, apiName), { exact: true });
    },
    [queryClient, serviceName],
  );

  return {
    apis,
    detailStatusByApiName,
    isLoading: apiNamesQuery.isLoading || isFetchingNextPage,
    isLoadingDetails: loadedDetailCount < apiNames.length,
    loadingProgress: { loaded: loadedDetailCount, total: apiNames.length },
    error: apiNamesQuery.isError ? describeFailure(`load APIs for ${serviceName}`, apiNamesQuery.error) : null,
    hasMore: Boolean(hasNextPage),
    loadMore,
    refresh,
    retryApiDetails,
  };
}
